import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { redirectToAuthFrom, selectCurrentUser, showLoginModal } from "../../../auth/authSlice";
import logging from 'utils/logging';
import GameGenerationProgress from './GameGenerationProgress';
import GameCompleteChoice from './GameCompleteChoice';
import Provider from 'provider';
import Analytics from 'analytics';
import { EVENTS } from 'analytics/events';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CreateHeader from '../CreateHeader';
import CreateWizard from './wizard/CreateWizard';
import { mapDifficulty, buildThemeWithGroup, AI_DEFAULT_TONE, AI_DEFAULT_HOST } from './buildAICreatePayload';
import '../create.css';
import {
  watchGameGenerationAction,
  stopWatchingGameGenerationAction,
  selectGeneration,
} from '../../createGame/createGameSlice';
import { clearAuthReturnPath, saveAuthReturnPath } from '../../../auth/authReturnPath';

const log = logging.getLogger('AICreate');
// Set log level explicitly to ensure logs appear
log.setLevel('DEBUG');

const AI_CREATE_DRAFT_KEY = 'groupardy.aiCreateDraft';
const AI_CREATE_PENDING_KEY = 'groupardy.aiCreatePending';

const loadSavedDraft = () => {
  try {
    return JSON.parse(localStorage.getItem(AI_CREATE_DRAFT_KEY)) || {};
  } catch (error) {
    log.warn("Failed to load saved AI create draft", error);
    return {};
  }
};

const saveDraft = (draft) => {
  localStorage.setItem(AI_CREATE_DRAFT_KEY, JSON.stringify(draft));
};

const clearPendingDraft = () => {
  localStorage.removeItem(AI_CREATE_DRAFT_KEY);
  localStorage.removeItem(AI_CREATE_PENDING_KEY);
};

const AICreate = () => {
  const savedDraft = loadSavedDraft();
  const [form, setForm] = useState({
    title: savedDraft.title || '',
    theme: savedDraft.theme || '',
    difficulty: savedDraft.difficulty || 'medium',
    difficultyOther: savedDraft.difficultyOther || '',
    length: savedDraft.length || 'normal',
  });

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedGameId, setGeneratedGameId] = useState(null);
  const [generatedGameName, setGeneratedGameName] = useState('');

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { gameId: urlGameId } = useParams();
  const [searchParams] = useSearchParams();
  const user = useSelector(selectCurrentUser);

  // Retry mode: when navigated to /manage/create/ai/:gameId?retry=true,
  // skip the form and jump straight to the progress screen.
  // GameGenerationProgress will detect generation.status === 'failed' and render ErrorState.
  const isRetry = searchParams.get('retry') === 'true' && !!urlGameId;

  // Done mode: when navigated to /manage/create/ai/:gameId/done, render the done screen.
  // This gives the done screen its own URL so browser back from /gameboard returns here.
  const isDone = !!urlGameId && location.pathname.endsWith('/done');

  // Progress mode: /manage/create/ai/:gameId/progress (or bare gameId with ?retry=true).
  // Renders GameGenerationProgress. Browser back from /done lands here.
  const isProgressUrl = !!urlGameId && location.pathname.endsWith('/progress');

  // For done mode, watch the game so we have the categoryCount / questionCount / etc.
  const generation = useSelector(selectGeneration);

  // Fire "create started" only when the creator lands on the form itself,
  // not when this component mounts for the progress / done / retry URLs.
  const isFormMode = !isProgressUrl && !isDone && !isRetry;
  useEffect(() => {
    if (isFormMode) {
      Analytics.track(EVENTS.GAME_CREATE_STARTED, { creation_mode: "ai" });
    }
    // Fire once on mount; mode is derived from the URL at mount time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isRetry || isProgressUrl) {
      setIsGenerating(true);
      setGeneratedGameId(urlGameId);
      // gameName will be populated from the Firestore listener inside GameGenerationProgress
      setGeneratedGameName('');
    }
  }, [isRetry, isProgressUrl, urlGameId]);

  useEffect(() => {
    if (isDone && urlGameId) {
      dispatch(watchGameGenerationAction(urlGameId));
    }
    return () => {
      if (isDone && urlGameId) {
        dispatch(stopWatchingGameGenerationAction(urlGameId));
      }
    };
  }, [isDone, urlGameId, dispatch]);

  const getGameLengthConfig = useCallback(() => {
    switch (form.length) {
      case 'short': return { rounds: 1, categoriesPerRound: 5, questionsPerCategory: 5 };
      case 'epic':  return { rounds: 3, categoriesPerRound: 6, questionsPerCategory: 6 };
      case 'normal':
      default:      return { rounds: 2, categoriesPerRound: 5, questionsPerCategory: 5 };
    }
  }, [form.length]);

  const buildDraft = useCallback(() => ({ ...form }), [form]);

  const buildFormValues = useCallback((currentUser) => {
    const cfg = getGameLengthConfig();
    return {
      name: form.title,
      theme: buildThemeWithGroup(form.theme, form.difficulty, form.difficultyOther),
      difficulty: mapDifficulty(form.difficulty),
      tone: AI_DEFAULT_TONE,
      host: AI_DEFAULT_HOST,
      customCategories: [],
      rounds: cfg.rounds,
      categoriesPerRound: cfg.categoriesPerRound,
      questionsPerCategory: cfg.questionsPerCategory,
      uid: currentUser.uid,
    };
  }, [form, getGameLengthConfig]);

  const startGameGeneration = useCallback(async (currentUser) => {
    const formValues = buildFormValues(currentUser);

    log.info("Starting game generation with values:", formValues);

    Analytics.track(EVENTS.GAME_CREATE_SUBMITTED, { creation_mode: "ai" });
    Analytics.track(EVENTS.GAME_CREATE_AI_PARAMS_SUBMITTED, {
      difficulty: formValues.difficulty,
      tone: AI_DEFAULT_TONE,
      host_personality: AI_DEFAULT_HOST,
      game_length_mode: form.length,
      rounds: formValues.rounds,
      category_count: formValues.categoriesPerRound,
      questions_per_category: formValues.questionsPerCategory,
      theme_prompt_length: (form.theme || '').length,
      custom_category_count: 0,
    });

    try {
      setIsGenerating(true);
      localStorage.removeItem(AI_CREATE_PENDING_KEY);

      // Call the AI game creation function
      const result = await Provider.createGameWithAI(formValues);
      clearPendingDraft();
      clearAuthReturnPath();

      // Set the game ID and name for progress tracking
      setGeneratedGameId(result.gameId);
      setGeneratedGameName(form.title);

      // Navigate to the progress URL so browser back returns to the form
      navigate(`/manage/create/ai/${result.gameId}/progress`);

      log.info("Game generation started successfully:", result);
    } catch (error) {
      log.error("Error generating game:", error);
      setIsGenerating(false);

      // Show error message to user
      alert(`Failed to start game generation: ${error.message || 'Unknown error'}`);
    }
  }, [buildFormValues, form, navigate]);

  useEffect(() => {
    if (user == null || isGenerating || !localStorage.getItem(AI_CREATE_PENDING_KEY)) {
      return;
    }

    startGameGeneration(user);
  }, [isGenerating, startGameGeneration, user]);

  const handleGenerateGame = async () => {
    if (user == null) {
      saveDraft(buildDraft());
      localStorage.setItem(AI_CREATE_PENDING_KEY, 'true');
      saveAuthReturnPath(`${location.pathname}${location.search}`);
      dispatch(redirectToAuthFrom({ destination: `${location.pathname}${location.search}` }));
      dispatch(showLoginModal());
      return;
    }

    await startGameGeneration(user);
  };

  const handleGenerationComplete = (gameId) => {
    setIsGenerating(false);
    setGeneratedGameId(null);
    setGeneratedGameName('');
  };

  // Cancel is fully owned by GameGenerationProgress: it shows a confirm dialog,
  // hard-deletes the game via Provider.deleteGame, and navigates to /manage.
  // AICreate unmounts on navigation, so no state reset is needed here.

  // Done screen — its own URL so back/forward navigation works
  if (isDone) {
    return (
      <div className="cg-page surface-manager">
        <CreateHeader />
        <GameCompleteChoice
          gameId={urlGameId}
          gameName={generation?.name || ''}
          categoryCount={generation?.categoryCount || 0}
          questionCount={generation?.questionCount || 0}
          roundCount={generation?.roundCount || 1}
          hasFinale={generation?.hasFinale !== false}
        />
      </div>
    );
  }

  // Show progress screen if generating
  if (isGenerating && generatedGameId) {
    return (
      <div className="cg-page surface-manager">
        <CreateHeader />
        <GameGenerationProgress
          gameId={generatedGameId}
          gameName={generatedGameName}
          onComplete={handleGenerationComplete}
        />
      </div>
    );
  }

  return (
    <div className="cg-page surface-manager">
      <CreateHeader />
      <main className="cg-main">
        <CreateWizard
          form={form}
          setForm={setForm}
          onSubmit={handleGenerateGame}
          onBack={() => navigate('/create')}
          submitting={isGenerating}
        />
      </main>
    </div>
  );
};

export default AICreate;
